import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Add, Check, Close, Edit, Delete } from '@mui/icons-material';
import { Box, IconButton, ToggleButton, ToggleButtonGroup, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Table, usePagination } from '@frontend/features/common/components/Table';
import { ListLayout } from '@frontend/features/common/components/ListLayout';
import { SearchInput } from '@frontend/features/common/components/SearchInput/SearchInput';
import { TABLE_HEIGHT_WITH_AGENCIES_LAYOUT } from '@frontend/features/agencies/constants';
import { useFollowUpRules, FollowUpRule, useDeleteFollowUpRule } from '@frontend/features/follow-ups/queries';
import { FollowUpModal } from '@frontend/features/follow-ups/components/FollowUpModal';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';
import { useGetWorkflowsByAgency, useGetStepsByManyWorkflowIds } from '@frontend/features/workflows/queries';

export const CaseTriggersList = () => {
  const { id } = useParams();
  const agencyId = Number(id);
  const pagination = usePagination();
  const { data, refetch, isLoading } = useFollowUpRules({
    agencyId,
    page: pagination.page,
    limit: pagination.rowsPerPage,
  });
  const { t } = useTranslation();
  const deleteMutation = useDeleteFollowUpRule(agencyId);
  const { addToast } = useSnackBar();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState<FollowUpRule | undefined>(undefined);

  const workflowIds = (data?.items || []).map((item) => item.scanWorkflow).filter(Boolean);
  const uniqueWorkflowIds = Array.from(new Set(workflowIds));

  const { data: workflowsData } = useGetWorkflowsByAgency({
    agencyId,
    pageSize: 1000,
    currentPage: 1,
    search: '',
  });
  const { data: stepsData } = useGetStepsByManyWorkflowIds({ workflowIds: uniqueWorkflowIds, search: '' });

  const workflowNameById = (workflowsData?.items || []).reduce<Record<string, string>>((acc, wf) => {
    acc[wf.id] = wf.name;
    return acc;
  }, {});

  const stepNameById = (stepsData || []).reduce<Record<string, string>>((acc, step) => {
    acc[step.id] = step.name;
    return acc;
  }, {});

  const renderStatus = (status?: string | null) => {
    if (!status) {
      return t('cases.status.any', { defaultValue: 'Any status (default: open)' });
    }
    const key = `cases.status.${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const handleModalOpen = (rule?: FollowUpRule) => () => {
    setSelectedRule(rule);
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setSelectedRule(undefined);
    setIsModalOpen(false);
    refetch();
  };

  const handleDelete = (rule: FollowUpRule) => async () => {
    const confirmed = window.confirm('Are you sure you want to delete this follow-up rule?');
    if (!confirmed) return;
    try {
      await deleteMutation.mutateAsync(rule.id);
      addToast({ message: 'Follow-up deleted', color: 'success' });
      refetch();
    } catch (e) {
      addToast({ message: 'Error deleting follow-up', color: 'error' });
    }
  };

  return (
    <ListLayout padding={0} title="Case triggers" subtitle="Automatic follow-up rules by workflow/step">
      <Box display="flex" alignItems="center" gap={(theme) => theme.spacing(3)} marginBottom={(theme) => theme.spacing(3)}>
        <Typography variant="subtitle1" align="left" sx={{ flexGrow: 1 }}>
          {data?.count || 0} results
        </Typography>

        <SearchInput placeholder="Search (not implemented)" onSearch={() => {}} />
        <ToggleButtonGroup
          exclusive
          sx={{
            height: (theme) => theme.spacing(5),
            '& button': {
              padding: (theme) => theme.spacing(1),
              width: (theme) => theme.spacing(5),
            },
          }}
        >
          <ToggleButton onClick={handleModalOpen()} value="add" aria-label="add case trigger">
            <Add />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isModalOpen && (
        <FollowUpModal open={isModalOpen} onClose={handleClose} agencyId={agencyId} rule={selectedRule} />
      )}

      {data?.count !== undefined && data.count > 0 && (
        <Table
          height={TABLE_HEIGHT_WITH_AGENCIES_LAYOUT}
          columns={[
            { id: 'actions', label: 'Actions', align: 'left' },
            { id: 'name', label: 'Name', align: 'left' },
            { id: 'workflow', label: 'Workflow', align: 'left' },
            { id: 'step', label: 'Step', align: 'left' },
            { id: 'status', label: 'Status', align: 'left' },
            { id: 'active', label: 'Active', align: 'left' },
          ]}
          data={{
            key: 'id',
            items: data.items.map((item) => ({
              ...item,
              workflow: workflowNameById[item.scanWorkflow] || item.scanWorkflow || '-',
              step: item.scanStep ? stepNameById[item.scanStep] || item.scanStep : '-',
              status: renderStatus(item.scanStatus),
              active: item.isActive ? <Check color="success" /> : <Close color="error" />,
              actions: (
                <>
                  <IconButton onClick={handleModalOpen(item)}>
                    <Edit color="primary" />
                  </IconButton>
                  <IconButton onClick={handleDelete(item)}>
                    <Delete color="error" />
                  </IconButton>
                </>
              ),
            })),
            totalCount: data.count,
          }}
          pagination={pagination}
          stickyHeader
        />
      )}

      {data?.count === 0 && !isLoading && (
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography>No case triggers yet.</Typography>
          <Button variant="contained" onClick={handleModalOpen()}>
            Create case trigger
          </Button>
        </Box>
      )}
    </ListLayout>
  );
};

export default CaseTriggersList;
